import { NgClass, PercentPipe } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { MatSuffix } from '@angular/material/form-field';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { ForecastVersionResponse } from '@api-types/responses/forecast-version.response';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  FormInputSelectionDirective,
  PmIconComponent,
  PmTooltipDirective,
} from '@libs/ui';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { selectIsLoggedInUserAdmin } from '@core/store/auth/auth.selectors';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { isForecastSeriesFetchingOrPending } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionsByConfigurationUrlParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import {
  selectConfigurationId,
  selectVersionId,
} from 'demand-forecasting/store/router-state/router.selectors';
import {
  LucideCircleCheck,
  LucideCircleX,
  LucidePlus,
  LucideSettings,
  LucideSquarePen,
} from '@lucide/angular';

import { twMerge } from 'tailwind-merge';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'pm-forecast-version-picker',
  templateUrl: './forecast-version-picker.component.html',
  styleUrls: ['./forecast-version-picker.component.scss'],
  imports: [
    MatSuffix,
    NgClass,
    TranslateModule,
    PercentPipe,
    PmIconComponent,
    FormFieldComponent,
    FormInputSelectComponent,
    FormsModule,
    FormInputSelectionDirective,
    PmTooltipDirective,
  ],
})
export class ForecastVersionPickerComponent {
  ForecastVersionStatus = ForecastVersionStatus;

  twMerge = twMerge;
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  protected dateUtilsService = inject(DateUtilsService);
  private readonly configurationRouterHelperService = inject(
    ConfigurationRouterHelperService,
  );

  forecastVersions = this.store.selectSignal(
    selectForecastVersionsByConfigurationUrlParam,
  );

  isLoading = computed(() => {
    const configurationsAreFetching = this.store.selectSignal(
      ConfigurationsSelectors.selectAreConfigurationsFetching,
    )();
    const forecastSeriesIsFetching = this.store.selectSignal(
      isForecastSeriesFetchingOrPending,
    )();
    return configurationsAreFetching || forecastSeriesIsFetching;
  });
  configurationId = this.store.selectSignal(selectConfigurationId);
  selectedVersionId = this.store.selectSignal(selectVersionId);

  isLoggedInUserAdmin = this.store.selectSignal(selectIsLoggedInUserAdmin);

  availableVersionOptions: Signal<NgxVirtualSelectFieldOptionModel<string>[]> =
    computed(() =>
      this.forecastVersions()
        .filter((version) => this.isLoggedInUserAdmin() || version.isVisible)
        .map((version) => {
          return {
            version,
            statusIcon: this.getStatusIcon(version),
            sortBy: version.createdAt,
            value: version.id,
            label: this.getFormattedCreatedAt(version.createdAt),
            tooltip: this.getResultSetVersionTooltipText(version),
            /**
             * @deprecated This field is only for temporary use in the Medicos Scenario Planning Light feature and will be removed in the future.
             */
            isMedicosScenarioPlanningLightVersion:
              version.isMedicosScenarioPlanningLightVersion,
          };
        }),
    );

  versionByIdMap: Signal<Map<string, ForecastVersionResponse>> = computed(
    () =>
      new Map<string, ForecastVersionResponse>(
        this.forecastVersions().map((version) => [version.id, version]),
      ),
  );

  getFormattedCreatedAt(createdAt: string | undefined): string {
    if (createdAt) {
      return this.dateUtilsService.formatDateBasedOnLanguage(createdAt);
    }
    return '';
  }

  protected readonly checkIcon = LucideCircleCheck;
  protected readonly cancelIcon = LucideCircleX;
  protected readonly settingsIcon = LucideSettings;
  protected readonly plusIcon = LucidePlus;
  protected readonly isMedicosScenarioPlanningLightVersionIcon =
    LucideSquarePen;

  getStatusIcon(version: ForecastVersionResponse) {
    const statusIconMap = {
      [ForecastVersionStatus.FINISHED]: this.checkIcon,
      [ForecastVersionStatus.FAILED]: this.cancelIcon,
      [ForecastVersionStatus.CALCULATING]: this.settingsIcon,
      [ForecastVersionStatus.CREATED]: this.plusIcon,
    };
    /**
     * @deprecated This condition is only for temporary use in the Medicos Scenario Planning Light feature and will be removed in the future.
     */
    if (
      version.status === ForecastVersionStatus.FINISHED &&
      version.isMedicosScenarioPlanningLightVersion
    ) {
      return this.isMedicosScenarioPlanningLightVersionIcon;
    }
    return statusIconMap[version.status] || '';
  }

  versionSelectionChanged(
    versionId: string | (string | undefined)[] | undefined,
  ): void {
    if (!versionId || Array.isArray(versionId)) {
      return;
    }
    this.configurationRouterHelperService.updateVersionId(versionId);
  }

  getResultSetVersionTooltipText(
    resultSetVersion: ForecastVersionResponse,
  ): string {
    const formattedCreatedAtTime =
      this.dateUtilsService.formatTimeBasedOnLanguage(
        resultSetVersion.createdAt,
      );

    const formattedDataFromDate =
      this.dateUtilsService.formatDateBasedOnLanguage(
        resultSetVersion.dataFrom,
      );

    return `${this.translate.instant(
      'RESULT_OVERVIEW.VERSION_PICKER.CREATED_AT_O_CLOCK',
    )}: ${formattedCreatedAtTime} ${this.translate.instant('RESULT_OVERVIEW.VERSION_PICKER.DATA_FROM')}: ${formattedDataFromDate}`;
  }
}
